import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InfoDisplay } from '@/components/ui/InfoRow';
import { formatDate } from '@/utils/dateUtils';
import { Customer } from '@/types';

interface CustomerCardProps {
  customer: Customer;
  editingCustomerId?: number | null;
  onEdit: (customer: Customer) => void;
  onServiceConnection: (customer: Customer) => void;
  onDeactivate: (customerId: number) => void;
  onActivate: (customerId: number) => void;
  onDelete: (customerId: number) => void;
}

export const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  editingCustomerId,
  onEdit,
  onServiceConnection,
  onDeactivate,
  onActivate,
  onDelete,
}) => {
  return (
    <Card
      className={`${editingCustomerId === customer.id ? "border-2 border-blue-500 shadow-lg" : ""} ${
        customer.is_deleted ? "opacity-50 bg-gray-100" : ""
      }`}
    >
      <CardHeader>
        <CardTitle className={`text-lg ${customer.is_deleted ? "text-gray-500" : ""}`}>
          {customer.customer_name}
          {customer.is_deleted && <span className="ml-2 text-xs text-red-500">(삭제됨)</span>}
        </CardTitle>
        <CardDescription className={customer.is_deleted ? "text-gray-400" : ""}>
          {customer.plan?.plan_name || '플랜 없음'} • {customer.customer_code}
          {!customer.is_active && !customer.is_deleted && (
            <span className="ml-2 text-xs text-yellow-600">(비활성화)</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <InfoDisplay
          layout="inline"
          spacing="tight"
          className={customer.is_deleted ? "text-gray-500" : ""}
          items={[
            { label: "고객사 코드", value: customer.customer_code },
            { label: "서브도메인", value: customer.alias || customer.customer_code },
            { label: "사용자", value: `${customer.active_user_count ?? 0}명 / ${customer.max_users}명` },
            { label: "서비스 URL", value: customer.service_url },
            { label: "계약 종료일", value: customer.contract_end_date },
            { label: "생성일", value: customer.created_at ? formatDate(customer.created_at) : '', hidden: !customer.created_at },
            { label: "담당자", value: customer.contact_name || '', hidden: !customer.contact_name },
            { label: "담당자 이메일", value: customer.contact_email || '', hidden: !customer.contact_email },
            { label: "담당자 연락처", value: customer.contact_phone || '', hidden: !customer.contact_phone },
            { label: "비고", value: customer.notes || '', hidden: !customer.notes },
          ]}
        />
        <div className="flex space-x-2 pt-4">
          {!customer.is_deleted && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(customer)}
                disabled={!customer.is_active}
              >
                수정
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onServiceConnection(customer)}
                disabled={!customer.is_active}
              >
                서비스 연결
              </Button>
              {customer.is_active ? (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => onDeactivate(customer.id)}
                >
                  비활성화
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onActivate(customer.id)}
                >
                  활성화
                </Button>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onDelete(customer.id)}
                disabled={customer.is_active}
              >
                삭제
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

